import { App, Modal } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fundsLine, kbApi, kbKeys, useFunds, type UploadItem } from '@entities/knowledge';
import { sourceApi } from '@entities/source';
import { watchKb } from '@features/kb-watch';
import { extractErrorMessage } from '@shared/api';
import { plural } from '@shared/ui/v2';
import { extOf, imagesWord, money, pagesWord, sizeMb, TypeBadge } from './kbUi';

const OK_EXT = ['pdf', 'docx', 'pptx', 'xlsx', 'txt'];
export const ACCEPT = '.pdf,.docx,.pptx,.xlsx,.txt,.zip';

type Row =
  | { kind: 'loading'; key: string; name: string; size: number; child?: boolean }
  | { kind: 'item'; key: string; item: UploadItem; child?: boolean }
  | {
      kind: 'zip';
      key: string;
      name: string;
      size: number;
      ok: number;
      skipped: number;
      ids: string[];
    }
  | { kind: 'skip'; key: string; name: string; reason: string; child?: boolean }
  | { kind: 'error'; key: string; name: string; reason: string; child?: boolean };

interface Props {
  assistantId: string;
  open: boolean;
  initialFiles?: File[];
  /** «Заменить»: после запуска новый файл заменит этот источник. */
  replaceId?: string | null;
  onClose: () => void;
}

/** Экран 5а «Загрузка документов» — окно поверх «Базы знаний» (утверждён 28.10.2026). */
export function UploadModal({ assistantId, open, initialFiles, replaceId, onClose }: Props) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: funds, refetch: refetchFunds } = useFunds();
  const [rows, setRows] = useState<Row[]>([]);
  const [images, setImages] = useState(true);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const started = useRef(false);
  // «Отмена» во время загрузки: ответы, пришедшие позже, относятся к закрытому
  // окну — такие файлы сразу убираем, иначе повиснут «Ждёт подтверждения».
  const gen = useRef(0);

  const replaceRow = (key: string, next: Row[]) =>
    setRows((rs) => {
      const i = rs.findIndex((r) => r.key === key);
      if (i < 0) return [...rs, ...next];
      return [...rs.slice(0, i), ...next, ...rs.slice(i + 1)];
    });

  const addFiles = (files: File[]) => {
    for (const f of files) {
      const key = `${f.name}-${f.size}-${Math.random()}`;
      const ext = extOf(f.name);
      if (ext !== 'zip' && !OK_EXT.includes(ext)) {
        setRows((rs) => [
          ...rs,
          { kind: 'skip', key, name: f.name, reason: 'Формат не поддерживается — пропустим' },
        ]);
        continue;
      }
      if (f.size > 100 * 1024 * 1024 && ext !== 'zip') {
        setRows((rs) => [
          ...rs,
          { kind: 'error', key, name: f.name, reason: 'Файл больше 100 МБ' },
        ]);
        continue;
      }
      setRows((rs) => [...rs, { kind: 'loading', key, name: f.name, size: f.size }]);
      const g = gen.current;
      const stale = (ids: string[]) => {
        if (g === gen.current) return false;
        ids.forEach((sid) => kbApi.removePending(sid).catch(() => undefined));
        return true;
      };
      const job =
        ext === 'zip'
          ? kbApi.uploadZip(assistantId, f).then((z) => {
              if (stale(z.items.map((it) => it.source_id))) return;
              const next: Row[] = [
                {
                  kind: 'zip',
                  key,
                  name: z.archive_name,
                  size: f.size,
                  ok: z.items.length,
                  skipped: z.skipped.length,
                  ids: z.items.map((it) => it.source_id),
                },
                ...z.items.map((it) => ({
                  kind: 'item' as const,
                  key: it.source_id,
                  item: it,
                  child: true,
                })),
                ...z.skipped.map((s, i) => ({
                  kind: s.reason.startsWith('Формат') ? ('skip' as const) : ('error' as const),
                  key: `${key}-s${i}`,
                  name: s.file_name,
                  reason: s.reason,
                  child: true,
                })),
              ];
              replaceRow(key, next);
            })
          : kbApi.uploadFile(assistantId, f).then((it) => {
              if (stale([it.source_id])) return;
              replaceRow(key, [{ kind: 'item', key: it.source_id, item: it }]);
            });
      job.catch((e) => {
        if (g !== gen.current) return;
        replaceRow(key, [
          {
            kind: 'error',
            key,
            name: f.name,
            reason: (extractErrorMessage(e) || 'Не удалось прочитать файл').replace(
              /^Не удалось распознать файл/,
              'Не удалось прочитать',
            ),
          },
        ]);
      });
    }
  };

  if (open && initialFiles?.length && !started.current) {
    started.current = true;
    setTimeout(() => addFiles(initialFiles), 0);
  }

  const items = rows
    .filter((r): r is Extract<Row, { kind: 'item' }> => r.kind === 'item')
    .map((r) => r.item);
  const loading = rows.some((r) => r.kind === 'loading');
  const pages = items.reduce((s, i) => s + i.page_count, 0);
  const imgs = items.reduce((s, i) => s + i.images_count, 0);
  const total = Math.round((pages * 0.5 + (images ? imgs : 0)) * 100) / 100;
  const fl = fundsLine(funds, total);
  const short = fl.short > 0;

  const remove = (r: Row) => {
    if (r.kind === 'zip') {
      // Архив целиком: строка архива и все файлы из него.
      const zipIdx = rows.findIndex((x) => x.key === r.key);
      let end = zipIdx + 1;
      while (end < rows.length && (rows[end] as { child?: boolean }).child) end += 1;
      setRows((rs) => [...rs.slice(0, zipIdx), ...rs.slice(end)]);
      r.ids.forEach((sid) => kbApi.removePending(sid).catch(() => undefined));
      return;
    }
    setRows((rs) => rs.filter((x) => x.key !== r.key));
    if (r.kind === 'item') kbApi.removePending(r.item.source_id).catch(() => undefined);
  };

  const zipPrice = (r: Extract<Row, { kind: 'zip' }>) => {
    const ids = new Set(r.ids);
    const its = items.filter((i) => ids.has(i.source_id));
    if (!its.length) return null;
    const sum = its.reduce(
      (a, i) =>
        a + Number(images ? i.estimated_cost_with_vision_rub : i.estimated_cost_without_vision_rub),
      0,
    );
    return money(sum, images && its.some((i) => i.images_count > 0));
  };

  const reset = () => {
    gen.current += 1;
    setRows([]);
    setImages(true);
    started.current = false;
  };

  const cancel = () => {
    // Загруженные, но не запущенные файлы убираем — иначе повиснут в «Ждёт подтверждения».
    items.forEach((i) => kbApi.removePending(i.source_id).catch(() => undefined));
    reset();
    onClose();
  };

  const start = async () => {
    if (!items.length) return;
    setBusy(true);
    try {
      await kbApi.confirmBatch(
        assistantId,
        items.map((i) => i.source_id),
        images,
      );
      if (replaceId) await sourceApi.delete(replaceId).catch(() => undefined);
      watchKb(
        assistantId,
        items.map((i) => i.source_id),
      );
      qc.invalidateQueries({ queryKey: kbKeys.funds });
      message.success(
        items.length === 1 ? 'Файл отправлен в обработку' : `Файлов в обработке: ${items.length}`,
      );
      qc.invalidateQueries({ queryKey: kbKeys.overview(assistantId) });
      qc.invalidateQueries({ queryKey: ['cabinet'] });
      reset();
      onClose();
    } catch (e) {
      const st = (e as { response?: { status?: number } }).response?.status;
      if (st === 402) {
        refetchFunds();
        message.warning('Не хватает денег — пополните баланс, загрузка начнётся сама');
      } else message.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // «Пополнить баланс»: файлы ждут оплаты (autostart) и стартуют сами после пополнения.
  const topup = async () => {
    try {
      await kbApi.confirmBatch(
        assistantId,
        items.map((i) => i.source_id),
        images,
      );
    } catch {
      /* 402 — ожидаемо: файлы помечены «ждут оплаты» */
    }
    qc.invalidateQueries({ queryKey: kbKeys.overview(assistantId) });
    reset();
    onClose();
    navigate('/billing?topup=1');
  };

  const count = items.length;
  const filesWord = plural(count, 'файл', 'файла', 'файлов');
  const avail = Number(funds?.available_rub ?? funds?.balance_rub ?? 0);
  const bonus = Number(funds?.bonus_left_rub ?? 0);

  return (
    <Modal
      open={open}
      onCancel={cancel}
      footer={null}
      width={640}
      title="Загрузка документов"
      destroyOnHidden
    >
      <div className="up-body">
        <div
          className={drag ? 'up-drop is-drag' : 'up-drop'}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            addFiles(Array.from(e.dataTransfer.files));
          }}
          onClick={() => input.current?.click()}
          role="button"
          tabIndex={0}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 16V4M7 9l5-5 5 5M4 20h16" />
          </svg>
          <div>
            <div style={{ fontWeight: 600 }}>
              Перетащите файлы или <u>выберите на компьютере</u>
            </div>
            <div className="v2-xs v2-muted">
              Можно несколько файлов сразу или ZIP-архив — распакуем сами · PDF, Word, PowerPoint,
              Excel, TXT · до 100 МБ
            </div>
          </div>
          <input
            ref={input}
            type="file"
            multiple
            accept={ACCEPT}
            hidden
            onChange={(e) => {
              addFiles(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />
        </div>

        {rows.length > 0 && (
          <div className="up-list">
            {/* Файлы внутри архива не показываем — только сам архив и итог распаковки. */}
            {rows
              .filter((r) => !('child' in r && r.child))
              .map((r) => (
                <div
                  key={r.key}
                  className={`up-row${'child' in r && r.child ? ' is-child' : ''}${r.kind === 'error' ? ' is-error' : ''}`}
                >
                  <TypeBadge
                    name={r.kind === 'item' ? r.item.file_name : r.name}
                    dim={r.kind === 'skip' || r.kind === 'error'}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="up-name">{r.kind === 'item' ? r.item.file_name : r.name}</div>
                    <div
                      className="v2-xs"
                      style={{
                        color:
                          r.kind === 'error'
                            ? '#DC2626'
                            : r.kind === 'skip'
                              ? '#B45309'
                              : '#6B7280',
                      }}
                    >
                      {r.kind === 'loading' && `${sizeMb(r.size)} · загружаем и считаем страницы…`}
                      {r.kind === 'item' &&
                        `${sizeMb(r.item.file_size_bytes)} · ${r.item.page_count} ${
                          extOf(r.item.file_name) === 'pptx'
                            ? plural(r.item.page_count, 'слайд', 'слайда', 'слайдов')
                            : pagesWord(r.item.page_count)
                        } · ${
                          r.item.images_count
                            ? `${r.item.images_count} ${imagesWord(r.item.images_count)}`
                            : 'без картинок'
                        }`}
                      {r.kind === 'zip' &&
                        `${sizeMb(r.size)} · распаковали: ${r.ok} ${plural(r.ok, 'файл подходит', 'файла подходят', 'файлов подходят')}${
                          r.skipped ? `, ${r.skipped} пропустим` : ''
                        }`}
                      {(r.kind === 'skip' || r.kind === 'error') && r.reason}
                    </div>
                  </div>
                  <div className="up-price">
                    {r.kind === 'zip' && zipPrice(r)}
                    {r.kind === 'item' &&
                      money(
                        images
                          ? r.item.estimated_cost_with_vision_rub
                          : r.item.estimated_cost_without_vision_rub,
                        images && r.item.images_count > 0,
                      )}
                    {r.kind === 'skip' && (
                      <span style={{ color: '#B45309', fontWeight: 400 }}>пропустим</span>
                    )}
                    {r.kind === 'error' && (
                      <span style={{ color: '#DC2626', fontWeight: 400 }}>не загрузится</span>
                    )}
                  </div>
                  {r.kind !== 'loading' && (
                    <button
                      type="button"
                      className="up-x"
                      aria-label={r.kind === 'zip' ? 'Убрать архив целиком' : 'Убрать'}
                      title={r.kind === 'zip' ? 'Убрать архив целиком' : 'Убрать'}
                      onClick={() => remove(r)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
          </div>
        )}

        {count > 0 && (
          <>
            <div className="up-switch-card">
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>Распознавать картинки</div>
                <div className="v2-xs v2-muted">
                  Схемы и фото из документов попадут в ответы ассистента. 1 ₽ за картинку —
                  декоративные (логотипы, фоны) не считаем и не берём за них деньги.
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={images}
                aria-label="Распознавать картинки"
                className={images ? 'st-switch is-on' : 'st-switch'}
                onClick={() => setImages((v) => !v)}
              >
                <span />
              </button>
            </div>
            <div className="up-sum">
              <div className="up-sum-row">
                <span>Страницы · {pages} × 0,5 ₽</span>
                <span>{money(pages * 0.5)}</span>
              </div>
              {imgs > 0 && (
                <div
                  className="up-sum-row"
                  style={images ? undefined : { color: '#9CA3AF', textDecoration: 'line-through' }}
                >
                  <span>Картинки · до {imgs} × 1 ₽</span>
                  <span>{money(imgs, true)}</span>
                </div>
              )}
              <div className="up-sum-total">
                <span>Спишем не больше</span>
                <span>{money(total)}</span>
              </div>
              <div className="v2-xs v2-muted">
                Точная сумма — после обработки, обычно меньше. Если обработать не получится — деньги
                вернём.
              </div>
              {!short && fl.text && <div className="v2-xs v2-muted">{fl.text}</div>}
            </div>
            {short && (
              <div role="alert" className="up-short">
                <div style={{ flex: 1, fontSize: 13 }}>
                  <b>Не хватает {money(fl.short)}.</b>{' '}
                  <span style={{ color: '#374151' }}>
                    {bonus > 0
                      ? `На балансе ${money(avail)}, из них приветственный бонус ${money(bonus)}.`
                      : `Приветственный бонус израсходован, на балансе ${money(avail)}.`}{' '}
                    Пополните баланс — загрузка начнётся сразу после оплаты.
                  </span>
                </div>
                <button type="button" className="v2-btn is-primary" onClick={topup}>
                  Пополнить баланс
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <div className="up-foot">
        <span className="v2-xs v2-muted" style={{ flex: 1 }}>
          {rows.some((r) => r.kind === 'skip' || r.kind === 'error')
            ? 'Файлы с ошибкой и неподходящего формата не загрузятся.'
            : ''}
        </span>
        <button type="button" className="v2-btn" onClick={cancel}>
          Отмена
        </button>
        <button
          type="button"
          className="v2-btn is-primary"
          disabled={!count || short || loading || busy}
          onClick={start}
        >
          {count
            ? `Загрузить ${count} ${filesWord} · ${money(total, images && imgs > 0)}`
            : 'Загрузить'}
        </button>
      </div>
    </Modal>
  );
}
